import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Us, De } from 'react-flags-select';
import './AdminPage.css';
import { API_BASE } from '../../config/api';
import EditorField from '../../components/EditorField/EditorField';

const sidebarItems = [
    { key: 'doctors', label: 'Doctors', addLabel: 'Add Doctor' },
    { key: 'notices', label: 'Notices', addLabel: 'Add Notice' },
    { key: 'openingTimes', label: 'Opening Times', addLabel: 'Add Opening Time' },
    { key: 'jobOpenings', label: 'Job Openings', addLabel: 'Add Job Opening' },
    { key: 'specialists', label: 'Specialists', addLabel: 'Add Specialist' },
    { key: 'referrals', label: 'Referrals', addLabel: 'Add Referral' },
    { key: 'prescriptionOrders', label: 'Prescription Orders', addLabel: 'Add Order' },
    { key: 'featureToggles', label: 'Order Toggles', addLabel: 'Save Toggles' },
];

const dayOptions = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const salutationOptions = ['MR', 'MRS', 'MS', 'DR', 'PROF_DR'];
const insuranceOptions = ['PUBLIC', 'PRIVATE'];
const prescriptionStatusOptions = ['PENDING', 'IN_PROGRESS', 'FULFILLED', 'CANCELLED'];
const referralStatusOptions = ['SUBMITTED', 'IN_REVIEW', 'APPROVED', 'COMPLETED', 'CANCELLED'];
const doctorDesignationOptions = [
    'General Practitioner - Arzt',
    'Medical assistent - Arzthelfer',
    'Medical professionals - Medizinische Fachangestellte',
];
const employmentTypeOptions = [
    'FULL_TIME',
    'PART_TIME',
    'MINI_JOB',
];

const defaultFeatureToggles = {
    prescriptionEnabled: true,
    appointmentEnabled: true,
    referralEnabled: true,
    phoneEnabled: true,
};

const createEmptyForms = () => ({
    doctors: { name: '', designation: '' },
    notices: {
        title: '',
        description: '',
        isActive: true,
    },
    applications: {},
    openingTimes: {
        dayOfWeek: 'MONDAY',
        openingTime: '08:00',
        closingTime: '13:30',
        eveningOpeningTime: '',
        eveningClosingTime: '',
        isClosed: false,
    },
    jobOpenings: {
        title: '',
        employmentType: ['FULL_TIME'],
        location: '',
        description: '',
        responsibilities: '',
        profile: '',
        benefits: '',
    },
    specialists: { nameEn: '', nameDe: '' },
    referrals: {
        specialistType: '',
        reason: '',
        urgency: '',
        salutation: 'MR',
        surname: '',
        firstName: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        insuranceMethod: 'PUBLIC',
        consentDataProcessing: false,
        consentMarketing: false,
        status: 'SUBMITTED',
        adminNotes: '',
        referralLetterPath: '',
    },
    prescriptionOrders: {
        salutation: 'MR',
        surname: '',
        firstName: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        insuranceMethod: 'PUBLIC',
        consentDataProcessing: false,
        consentMarketing: false,
        status: 'PENDING',
        notes: '',
        adminNotes: '',
        estimatedReadyTime: '',
        fulfilledAt: '',
        selectedMedicationIds: [],
    },
    featureToggles: { ...defaultFeatureToggles },
});

const createEmptyData = () => ({
    doctors: [],
    medications: [],
    notices: [],
    applications: [],
    openingTimes: [],
    jobOpenings: [],
    specialists: [],
    referrals: [],
    prescriptionOrders: [],
    featureToggles: [],
});

const createEmptyEditingState = () => ({
    doctors: null,
    notices: null,
    openingTimes: null,
    jobOpenings: null,
    applications: null,
    specialists: null,
    referrals: null,
    prescriptionOrders: null,
});

const sectionMeta = {
    doctors: {
        title: 'Doctors',
        description: 'Manage doctor records in a table and update them from one place.',
    },
    notices: {
        title: 'Notices',
        description: 'Keep notices organized in a table with direct edit and delete actions.',
    },
    openingTimes: {
        title: 'Opening Times',
        description: 'Maintain daily opening hours with optional evening slots in a structured table.',
    },
    jobOpenings: {
        title: 'Job Openings',
        description: 'Publish and manage job openings shown on the career page.',
    },
    applications: {
        title: 'Applications',
        description: 'Review submitted job applications and download resumes.',
    },
    specialists: {
        title: 'Specialists',
        description: 'Maintain the specialist directory used by the referral flow.',
    },
    referrals: {
        title: 'Referrals',
        description: 'Review and manage submitted referral requests in a table format.',
    },
    prescriptionOrders: {
        title: 'Prescription Orders',
        description: 'Review and manage submitted prescription requests in a table format.',
    },
    featureToggles: {
        title: 'Order Toggles',
        description: 'Enable or disable public order actions shown on the site.',
    },
};

const request = async (path, { method = 'GET', token, body } = {}) => {
    const response = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
    });

    if (response.status === 204) return null;

    const data = await response.json().catch(() => null);
    if (!response.ok) {
        const error = new Error(data?.message || `Request failed (${response.status})`);
        error.status = response.status;
        throw error;
    }

    return data;
};

const toLocalDateTimeInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const toIsoOrNull = (value) => (value ? new Date(value).toISOString() : null);

const toLocalDateInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
};

const formatDateOnly = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
};

const formatStatusOptionLabel = (value) =>
    value
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

const normalizeEmploymentTypes = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    return [];
};

const formatEmploymentTypeLabel = (value) => {
    const normalized = normalizeEmploymentTypes(value);
    if (!normalized.length) return '-';
    return normalized.map((item) => formatStatusOptionLabel(item)).join(', ');
};

const sanitizeHtml = (value) => {
    if (!value) return '';
    if (typeof window === 'undefined') return String(value);

    const doc = new DOMParser().parseFromString(String(value), 'text/html');
    doc.querySelectorAll('script, style').forEach((node) => node.remove());

    doc.body.querySelectorAll('*').forEach((node) => {
        [...node.attributes].forEach((attr) => {
            const name = attr.name.toLowerCase();
            const attrValue = String(attr.value || '').trim().toLowerCase();

            if (name.startsWith('on')) {
                node.removeAttribute(attr.name);
            }

            if (name === 'href' && attrValue) {
                try {
                    const parsed = new URL(attrValue, window.location.origin);
                    if (parsed.protocol.replace(':', '') === 'javascript') {
                        node.removeAttribute(attr.name);
                    }
                } catch (_error) {}
            }
        });
    });

    return doc.body.innerHTML;
};

const germanLevelOrder = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const getGermanLevelRank = (levels) => {
    if (!levels) return -1;
    const normalized = Array.isArray(levels) ? levels : [levels];
    const rank = normalized.reduce((current, level) => {
        if (!level) return current;
        const index = germanLevelOrder.indexOf(String(level).trim().toUpperCase());
        return index > current ? index : current;
    }, -1);
    return rank;
};

const getFormValuesForSection = (section, record) => {
    switch (section) {
        case 'doctors':
            return {
                name: record.name || '',
                designation: record.designation || '',
            };
        case 'notices':
            return {
                title: record.title || '',
                description: record.description || '',
                isActive: record.isActive ?? true,
            };
        case 'openingTimes':
            return {
                dayOfWeek: record.dayOfWeek || 'MONDAY',
                openingTime: record.openingTime || '08:00',
                closingTime: record.closingTime || '13:30',
                eveningOpeningTime: record.eveningOpeningTime || '',
                eveningClosingTime: record.eveningClosingTime || '',
                isClosed: record.isClosed ?? false,
            };
        case 'jobOpenings':
            return {
                title: record.title || '',
                employmentType: normalizeEmploymentTypes(record.employmentType).length
                    ? normalizeEmploymentTypes(record.employmentType)
                    : ['FULL_TIME'],
                location: record.location || '',
                description: record.description || '',
                responsibilities: record.responsibilities || '',
                profile: record.profile || '',
                benefits: record.benefits || '',
                isActive: record.isActive ?? true,
            };
        case 'specialists':
            return { nameEn: record.nameEn || '', nameDe: record.nameDe || '' };
        case 'referrals':
            return {
                specialistType: record.specialistType || '',
                reason: record.reason || '',
                urgency: record.urgency || '',
                salutation: record.salutation || 'MR',
                surname: record.surname || '',
                firstName: record.firstName || '',
                dateOfBirth: toLocalDateInput(record.dateOfBirth),
                phone: record.phone || '',
                email: record.email || '',
                insuranceMethod: record.insuranceMethod || 'PUBLIC',
                consentDataProcessing: record.consentDataProcessing ?? false,
                consentMarketing: record.consentMarketing ?? false,
                status: record.status || 'SUBMITTED',
                adminNotes: record.adminNotes || '',
                referralLetterPath: record.referralLetterPath || '',
            };
        case 'prescriptionOrders':
            return {
                salutation: record.salutation || 'MR',
                surname: record.surname || '',
                firstName: record.firstName || '',
                dateOfBirth: toLocalDateInput(record.dateOfBirth),
                phone: record.phone || '',
                email: record.email || '',
                insuranceMethod: record.insuranceMethod || 'PUBLIC',
                consentDataProcessing: record.consentDataProcessing ?? false,
                consentMarketing: record.consentMarketing ?? false,
                status: record.status || 'PENDING',
                notes: record.notes || '',
                adminNotes: record.adminNotes || '',
                estimatedReadyTime: toLocalDateTimeInput(record.estimatedReadyTime),
                fulfilledAt: toLocalDateTimeInput(record.fulfilledAt),
                selectedMedicationIds: (record.prescriptionItems || []).map((item) => item.medicationId),
            };
        default:
            return {};
    }
};

const buildPayload = (section, formValues) => {
    switch (section) {
        case 'doctors':
            return {
                name: formValues.name.trim(),
                designation: formValues.designation.trim(),
            };
        case 'notices':
            return {
                title: formValues.title.trim(),
                description: formValues.description.trim(),
                isActive: Boolean(formValues.isActive),
                validFrom: new Date().toISOString(),
                validTo: null,
                showTime: null,
                priority: 1,
            };
        case 'openingTimes':
            return {
                dayOfWeek: formValues.dayOfWeek,
                openingTime: formValues.isClosed ? null : formValues.openingTime || null,
                closingTime: formValues.isClosed ? null : formValues.closingTime || null,
                eveningOpeningTime: formValues.isClosed ? null : formValues.eveningOpeningTime || null,
                eveningClosingTime: formValues.isClosed ? null : formValues.eveningClosingTime || null,
                isClosed: Boolean(formValues.isClosed),
            };
        case 'jobOpenings':
            return {
                title: formValues.title.trim(),
                employmentType: normalizeEmploymentTypes(formValues.employmentType),
                location: formValues.location.trim(),
                description: formValues.description.trim(),
                responsibilities: formValues.responsibilities.trim(),
                profile: formValues.profile.trim(),
                benefits: formValues.benefits.trim(),
                isActive: Boolean(formValues.isActive),
            };
        case 'specialists':
            return { nameEn: formValues.nameEn.trim(), nameDe: formValues.nameDe.trim() };
        case 'referrals':
            return {
                specialistType: formValues.specialistType.trim(),
                reason: formValues.reason.trim(),
                urgency: formValues.urgency.trim() || null,
                salutation: formValues.salutation,
                surname: formValues.surname.trim(),
                firstName: formValues.firstName.trim(),
                dateOfBirth: toIsoOrNull(formValues.dateOfBirth) || new Date().toISOString(),
                phone: formValues.phone.trim() || null,
                email: formValues.email.trim(),
                insuranceMethod: formValues.insuranceMethod,
                consentDataProcessing: Boolean(formValues.consentDataProcessing),
                consentMarketing: Boolean(formValues.consentMarketing),
                status: formValues.status,
                adminNotes: formValues.adminNotes.trim() || null,
                referralLetterPath: formValues.referralLetterPath.trim() || null,
            };
        case 'prescriptionOrders':
            return {
                salutation: formValues.salutation,
                surname: formValues.surname.trim(),
                firstName: formValues.firstName.trim(),
                dateOfBirth: toIsoOrNull(formValues.dateOfBirth) || new Date().toISOString(),
                phone: formValues.phone.trim() || null,
                email: formValues.email.trim(),
                insuranceMethod: formValues.insuranceMethod,
                consentDataProcessing: Boolean(formValues.consentDataProcessing),
                consentMarketing: Boolean(formValues.consentMarketing),
                status: formValues.status,
                notes: formValues.notes.trim() || null,
                adminNotes: formValues.adminNotes.trim() || null,
                estimatedReadyTime: toIsoOrNull(formValues.estimatedReadyTime),
                fulfilledAt: toIsoOrNull(formValues.fulfilledAt),
                prescriptionItems: (formValues.selectedMedicationIds || []).map((medicationId) => ({
                    medicationId,
                    quantity: 1,
                })),
            };
        default:
            return {};
    }
};


const AdminPage = () => {
    const { t, i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

    const handleLanguageChange = (lang) => {
        setCurrentLanguage(lang);
        i18n.changeLanguage(lang);
    };

    const sidebarItemsTranslated = useMemo(() => [
        { key: 'doctors', label: t('admin_doctors'), addLabel: t('admin_add_doctor') },
        { key: 'notices', label: t('admin_notices'), addLabel: t('admin_add_notice') },
        { key: 'openingTimes', label: t('admin_opening_times'), addLabel: t('admin_add_opening_time') },
        { key: 'jobOpenings', label: t('admin_job_openings'), addLabel: t('admin_add_job_opening') },
        { key: 'applications', label: t('admin_applications'), addLabel: t('admin_add_application') },
        { key: 'specialists', label: t('admin_specialists'), addLabel: t('admin_add_specialist') },
        { key: 'referrals', label: t('admin_referrals'), addLabel: t('admin_add_referral') },
        { key: 'prescriptionOrders', label: t('admin_prescription_orders'), addLabel: t('admin_add_order') },
        { key: 'featureToggles', label: t('admin_feature_toggles'), addLabel: t('admin_save_changes') },
    ], [t]);

    const sectionMetaTranslated = useMemo(() => ({
        doctors: {
            title: t('admin_doctors'),
            description: t('admin_doctors_desc'),
        },
        notices: {
            title: t('admin_notices'),
            description: t('admin_notices_desc'),
        },
        openingTimes: {
            title: t('admin_opening_times'),
            description: t('admin_opening_times_desc'),
        },
        jobOpenings: {
            title: t('admin_job_openings'),
            description: t('admin_job_openings_desc'),
        },
        applications: {
            title: t('admin_applications'),
            description: t('admin_applications_desc'),
        },
        specialists: {
            title: t('admin_specialists'),
            description: t('admin_specialists_desc'),
        },
        referrals: {
            title: t('admin_referrals'),
            description: t('admin_referrals_desc'),
        },
        prescriptionOrders: {
            title: t('admin_prescription_orders'),
            description: t('admin_prescription_orders_desc'),
        },
        featureToggles: {
            title: t('admin_feature_toggles'),
            description: t('admin_feature_toggles_desc'),
        },
    }), [t]);

    const [email, setEmail] = useState('');

    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
    const [admin, setAdmin] = useState(null);
    const [activeSection, setActiveSection] = useState('doctors');
    const [openFormSection, setOpenFormSection] = useState(null);
    const [data, setData] = useState(createEmptyData);
    const [forms, setForms] = useState(createEmptyForms);
    const [editingIds, setEditingIds] = useState(createEmptyEditingState);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isDesignationMenuOpen, setIsDesignationMenuOpen] = useState(false);
    const [activeStatusMenu, setActiveStatusMenu] = useState(null);
    const [viewingPrescriptionOrder, setViewingPrescriptionOrder] = useState(null);
    const [viewingReferral, setViewingReferral] = useState(null);
    const [viewingApplicationNote, setViewingApplicationNote] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [applicationsSort, setApplicationsSort] = useState({ key: 'germanLevel', direction: 'asc' });
    const designationSelectRef = useRef(null);

    const isLoggedIn = useMemo(() => Boolean(token), [token]);

    const clearFlash = () => {
        setError('');
        setMessage('');
    };

    const resetSectionForm = useCallback((section) => {
        const emptyForms = createEmptyForms();
        setForms((current) => ({ ...current, [section]: emptyForms[section] }));
        setEditingIds((current) => ({ ...current, [section]: null }));
    }, []);

    const resetDashboardState = useCallback(() => {
        setAdmin(null);
        setData(createEmptyData());
        setForms(createEmptyForms());
        setEditingIds(createEmptyEditingState());
        setActiveSection('doctors');
        setOpenFormSection(null);
        setActiveStatusMenu(null);
    }, []);

    const toggleApplicationsSort = (key) => {
        setApplicationsSort((current) => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const getApplicationSortValue = (application, key) => {
        switch (key) {
            case 'name':
                return `${application.firstName || ''} ${application.lastName || ''}`.trim();
            case 'email':
                return application.email || '';
            case 'phone':
                return application.phone || '';
            case 'address':
                return application.address || '';
            case 'startDate':
                return application.earliestStartDate ? new Date(application.earliestStartDate) : null;
            case 'germanLevel': {
                const rank = getGermanLevelRank(application.germanLanguageLevels);
                return rank >= 0 ? rank : null;
            }
            case 'workAuthorized':
                return application.workAuthorization === undefined ? null : application.workAuthorization ? 1 : 0;
            case 'gdpr':
                return application.gdprConsent === undefined ? null : application.gdprConsent ? 1 : 0;
            case 'notes':
                return application.message || '';
            case 'resume':
                return application.resumeOriginalName || application.resumePath || '';
            case 'jobTitle':
                return application.jobOpening?.title || '';
            case 'submitted':
                return application.createdAt ? new Date(application.createdAt) : null;
            default:
                return '';
        }
    };

    const compareSortValues = (firstValue, secondValue, direction) => {
        const firstMissing = firstValue === null || firstValue === undefined || firstValue === '';
        const secondMissing = secondValue === null || secondValue === undefined || secondValue === '';

        if (firstMissing && secondMissing) return 0;
        if (firstMissing) return 1;
        if (secondMissing) return -1;

        if (firstValue instanceof Date && secondValue instanceof Date) {
            return (firstValue.getTime() - secondValue.getTime()) * direction;
        }

        if (typeof firstValue === 'number' && typeof secondValue === 'number') {
            return (firstValue - secondValue) * direction;
        }

        return String(firstValue).localeCompare(String(secondValue), undefined, {
            numeric: true,
            sensitivity: 'base',
        }) * direction;
    };

    const sortedApplications = useMemo(() => {
        const items = [...data.applications];
        const direction = applicationsSort.direction === 'asc' ? 1 : -1;
        const sortKey = applicationsSort.key;

        return items.sort((first, second) => {
            const firstValue = getApplicationSortValue(first, sortKey);
            const secondValue = getApplicationSortValue(second, sortKey);
            const primary = compareSortValues(firstValue, secondValue, direction);

            if (primary !== 0) return primary;

            const firstName = `${first.firstName || ''} ${first.lastName || ''}`.trim();
            const secondName = `${second.firstName || ''} ${second.lastName || ''}`.trim();
            return firstName.localeCompare(secondName) * direction;
        });
    }, [applicationsSort.direction, applicationsSort.key, data.applications]);

    const loadDashboardData = useCallback(async (authToken) => {
        const [
            adminResponse,
            doctorsResponse,
            medicationsResponse,
            noticesResponse,
            openingTimesResponse,
            jobOpeningsResponse,
            applicationsResponse,
            specialistsResponse,
            referralsResponse,
            prescriptionOrdersResponse,
            featureTogglesResponse,
        ] = await Promise.all([
            request('/auth/me', { token: authToken }),
            request('/doctors?limit=1000'),
            request('/medications?limit=1000'),
            request('/notices/all', { token: authToken }),
            request('/opening-times'),
            request('/job-openings/admin?limit=1000', { token: authToken }),
            request('/applications?limit=1000', { token: authToken }),
            request('/specialists?limit=1000', { token: authToken }),
            request('/referrals?limit=1000', { token: authToken }),
            request('/prescriptions?limit=1000', { token: authToken }),
            request('/feature-toggles/admin', { token: authToken }),
        ]);

        setAdmin(adminResponse);
        setData({
            doctors: doctorsResponse.data || [],
            medications: medicationsResponse.data || [],
            notices: noticesResponse || [],
            applications: applicationsResponse?.data || applicationsResponse || [],
            openingTimes: openingTimesResponse || [],
            jobOpenings: jobOpeningsResponse.data || [],
            specialists: specialistsResponse.data || [],
            referrals: referralsResponse.data || [],
            prescriptionOrders: prescriptionOrdersResponse.data || [],
            featureToggles: featureTogglesResponse ? [featureTogglesResponse] : [],
        });
        setForms((current) => ({
            ...current,
            featureToggles: {
                ...defaultFeatureToggles,
                ...featureTogglesResponse,
            },
        }));
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!token) {
            resetDashboardState();
            return;
        }

        let ignore = false;

        const bootstrap = async () => {
            setLoading(true);
            try {
                await loadDashboardData(token);
            } catch (err) {
                if (ignore) return;
                if (err?.status === 401 || err?.status === 403) {
                    localStorage.removeItem('adminToken');
                    setToken('');
                    setError(err.message || 'Session expired. Please sign in again.');
                } else {
                    setError(err?.message || 'Unable to load dashboard data.');
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        bootstrap();

        return () => {
            ignore = true;
        };
    }, [token, loadDashboardData, resetDashboardState]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (designationSelectRef.current && !designationSelectRef.current.contains(event.target)) {
                setIsDesignationMenuOpen(false);
            }

            if (!event.target.closest('.admin-status-trigger') && !event.target.closest('.admin-status-portal')) {
                setActiveStatusMenu(null);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, []);

    useEffect(() => {
        if (!activeStatusMenu) return undefined;

        const handleViewportChange = () => setActiveStatusMenu(null);

        window.addEventListener('scroll', handleViewportChange, true);
        window.addEventListener('resize', handleViewportChange);

        return () => {
            window.removeEventListener('scroll', handleViewportChange, true);
            window.removeEventListener('resize', handleViewportChange);
        };
    }, [activeStatusMenu]);

    const handleLogin = async (event) => {
        event.preventDefault();
        setLoading(true);
        clearFlash();

        try {
            const response = await request('/auth/login', {
                method: 'POST',
                body: { email, password },
            });

            localStorage.setItem('adminToken', response.token);
            setToken(response.token);
            setAdmin(response.admin || null);
            setMessage('Signed in successfully.');
        } catch (err) {
            setError(err.message || t('admin_login_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        clearFlash();
        localStorage.removeItem('adminToken');
        setToken('');
        setEmail('');
        setPassword('');
        setShowPassword(false);
        resetDashboardState();
        setMessage('');
    };

    const updateFormValue = (section, field, value) => {
        setForms((current) => ({
            ...current,
            [section]: {
                ...current[section],
                [field]: value,
            },
        }));
    };

    const toggleJobOpeningEmploymentType = (value) => {
        setForms((current) => {
            const currentTypes = normalizeEmploymentTypes(current.jobOpenings.employmentType);
            const nextTypes = currentTypes.includes(value)
                ? currentTypes.filter((item) => item !== value)
                : [...currentTypes, value];

            return {
                ...current,
                jobOpenings: {
                    ...current.jobOpenings,
                    employmentType: nextTypes,
                },
            };
        });
    };

    const handleOpenCreate = (section) => {
        clearFlash();
        setActiveSection(section);
        resetSectionForm(section);
        setOpenFormSection(section);
        setIsDesignationMenuOpen(false);
        setActiveStatusMenu(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleEdit = (section, record) => {
        clearFlash();
        setActiveSection(section);
        setEditingIds((current) => ({ ...current, [section]: record.id }));
        setForms((current) => ({
            ...current,
            [section]: getFormValuesForSection(section, record),
        }));
        setOpenFormSection(section);
        setIsDesignationMenuOpen(false);
        setActiveStatusMenu(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewPrescriptionOrder = (order) => {
        setViewingPrescriptionOrder(order);
    };

    const handleCloseViewModal = () => {
        setViewingPrescriptionOrder(null);
    };

    const handleViewReferral = (referral) => {
        setViewingReferral(referral);
    };

    const handleCloseReferralModal = () => {
        setViewingReferral(null);
    };

    const handleCloseApplicationNote = () => {
        setViewingApplicationNote(null);
    };

    const truncateText = (value, maxLength = 80) => {
        if (!value) return '';
        const normalized = String(value).trim();
        if (normalized.length <= maxLength) return normalized;
        return `${normalized.slice(0, maxLength).trim()}...`;
    };

    const handleCancel = (section) => {
        resetSectionForm(section);
        setOpenFormSection(null);
        setIsDesignationMenuOpen(false);
        setActiveStatusMenu(null);
    };

    const handleSubmitSection = async (event) => {
        event.preventDefault();
        if (!token) return;

        const editingId = editingIds[activeSection];
        const payload = buildPayload(activeSection, forms[activeSection]);
        const basePaths = {
            doctors: '/doctors',
            notices: '/notices',
            openingTimes: '/opening-times',
            jobOpenings: '/job-openings',
            applications: '/applications',
            specialists: '/specialists',
            referrals: '/referrals',
            prescriptionOrders: '/prescriptions',
        };

        const sectionLabel = sectionMeta[activeSection].title;
        const method = editingId ? 'PATCH' : 'POST';
        const path = editingId ? `${basePaths[activeSection]}/${editingId}` : basePaths[activeSection];

        setSubmitting(true);
        clearFlash();

        try {
            await request(path, {
                method,
                token,
                body: payload,
            });

            await loadDashboardData(token);
            resetSectionForm(activeSection);
            setOpenFormSection(null);
            setIsDesignationMenuOpen(false);
            setActiveStatusMenu(null);
            setMessage(editingId ? t('admin_updated_successfully', { item: sectionLabel }) : t('admin_created_successfully', { item: sectionLabel }));
        } catch (err) {
            setError(err.message || `Unable to save ${sectionLabel.toLowerCase()}.`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveFeatureToggles = async () => {
        if (!token) return;

        setSubmitting(true);
        clearFlash();

        try {
            await request('/feature-toggles', {
                method: 'PATCH',
                token,
                body: forms.featureToggles,
            });

            await loadDashboardData(token);
            setMessage(t('admin_feature_toggles_saved'));
        } catch (err) {
            setError(err.message || t('admin_feature_toggles_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (section, id) => {
        if (!token) return;

        const basePaths = {
            doctors: '/doctors',
            notices: '/notices',
            openingTimes: '/opening-times',
            jobOpenings: '/job-openings',
            specialists: '/specialists',
            referrals: '/referrals',
            prescriptionOrders: '/prescriptions',
        };

        setSubmitting(true);
        clearFlash();

        try {
            await request(`${basePaths[section]}/${id}`, {
                method: 'DELETE',
                token,
            });

            await loadDashboardData(token);
            if (editingIds[section] === id) {
                resetSectionForm(section);
                setOpenFormSection(null);
            }
            setMessage(t('admin_deleted_successfully', { item: sectionMetaTranslated[section].title }));
        } catch (err) {
            setError(err.message || t('admin_unable_to_delete', { item: sectionMetaTranslated[section].title }));
        } finally {
            setSubmitting(false);
        }
    };

    const handleQuickStatusUpdate = async (section, id, status) => {
        if (!token) return;

        const basePaths = {
            referrals: '/referrals',
            prescriptionOrders: '/prescriptions',
        };

        if (!basePaths[section]) return;

        setSubmitting(true);
        clearFlash();

        try {
            await request(`${basePaths[section]}/${id}`, {
                method: 'PATCH',
                token,
                body: { status },
            });

            await loadDashboardData(token);
            setActiveStatusMenu(null);
            setMessage(`${sectionMeta[section].title} status updated successfully.`);
        } catch (err) {
            setError(err.message || `Unable to update ${sectionMeta[section].title.toLowerCase()} status.`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleNoticeActiveToggle = async (id, isActive) => {
        if (!token) return;

        setSubmitting(true);
        clearFlash();

        try {
            await request(`/notices/${id}`, {
                method: 'PATCH',
                token,
                body: { isActive: Boolean(isActive) },
            });

            await loadDashboardData(token);
            if (editingIds.notices === id) {
                updateFormValue('notices', 'isActive', Boolean(isActive));
            }
            setMessage(t('admin_updated_successfully', { item: sectionMetaTranslated.notices.title }));
        } catch (err) {
            setError(err.message || `Unable to update ${sectionMetaTranslated.notices.title.toLowerCase()}.`);
        } finally {
            setSubmitting(false);
        }
    };

    const isStatusMenuOpen = (section, id) => activeStatusMenu?.section === section && activeStatusMenu?.id === id;

    const toggleStatusMenu = (event, section, id, currentValue, options) => {
        if (isStatusMenuOpen(section, id)) {
            setActiveStatusMenu(null);
            return;
        }

        const triggerRect = event.currentTarget.getBoundingClientRect();

        setActiveStatusMenu({
            section,
            id,
            currentValue,
            options,
            left: triggerRect.left,
            width: triggerRect.width,
            top: triggerRect.bottom + 8,
            shouldOpenUp: false,
        });
    };

    const renderFormActions = (section) => (
        <div className='admin-form-actions'>
            <button type='submit' disabled={submitting} className='admin-btn'>
                {submitting ? t('admin_saving') : editingIds[section] ? t('admin_update') : t('admin_create')}
            </button>
            <button type='button' disabled={submitting} onClick={() => handleCancel(section)} className='admin-btn admin-btn-ghost'>
                {t('admin_cancel')}
            </button>
        </div>
    );

    const renderSectionCard = (section, formTitle, formContent) => {
        if (openFormSection !== section) return null;

        return (
            <div
                className={`admin-card admin-form-card ${section === 'doctors' && isDesignationMenuOpen ? 'is-dropdown-open' : ''}`}
            >
                <p className='admin-section-kicker'>{sectionMeta[section].title}</p>
                <h3>{formTitle}</h3>
                <form className='admin-form' onSubmit={handleSubmitSection}>
                    {formContent}
                    {renderFormActions(section)}
                </form>
            </div>
        );
    };

    const renderTable = (section, columns, rows, emptyText) => {
        const activeItem = sidebarItems.find((item) => item.key === section);
        const showAddButton = section !== 'applications';
        const showActions = section !== 'applications';
        const isRequestSection = section === 'referrals' || section === 'prescriptionOrders';
        const tableScrollClassName = [
            'admin-table-scroll',
            isRequestSection ? 'is-requests' : '',
            section === 'referrals' ? 'is-referrals' : '',
            section === 'prescriptionOrders' ? 'is-prescription-orders' : '',
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div className='admin-card'>
                <div className='admin-table-toolbar'>
                    <div>
                        <h3>{sectionMeta[section].title} List</h3>
                        <p className='admin-section-text'>{data[section].length} records</p>
                    </div>
                    {showAddButton && (
                        <button type='button' className='admin-btn' onClick={() => handleOpenCreate(section)}>
                            {activeItem?.addLabel || 'Add'}
                        </button>
                    )}
                </div>

                {data[section].length ? (
                    <div className={tableScrollClassName}>
                        <div className='admin-table-wrap'>
                            <table className='admin-data-table'>
                                <thead>
                                    <tr>
                                        {columns.map((column, index) => {
                                            if (typeof column === 'string') {
                                                return <th key={`${column}-${index}`}>{column}</th>;
                                            }

                                            const label = column?.label || '';
                                            const columnKey = column?.key || `${section}-col-${index}`;
                                            const ariaSort = column?.ariaSort || 'none';
                                            const indicator = column?.sortIndicatorClass ? (
                                                <span
                                                    className={`admin-sort-indicator ${column.sortIndicatorClass}`}
                                                    aria-hidden='true'
                                                />
                                            ) : null;

                                            return (
                                                <th
                                                    key={columnKey}
                                                    className={column?.className}
                                                    aria-sort={ariaSort}
                                                >
                                                    {column?.onClick ? (
                                                        <button
                                                            type='button'
                                                            className='admin-sort-button'
                                                            onClick={column.onClick}
                                                        >
                                                            <span className='admin-sort-label'>{label}</span>
                                                            {indicator}
                                                        </button>
                                                    ) : (
                                                        label
                                                    )}
                                                </th>
                                            );
                                        })}
                                        {showActions && <th className='admin-actions-column'>{t('admin_actions')}</th>}
                                    </tr>
                                </thead>
                                <tbody>{rows}</tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <p className='admin-empty'>{emptyText}</p>
                )}
            </div>
        );
    };

    const renderDoctorsContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'doctors',
                editingIds.doctors ? 'Edit doctor' : 'Add doctor',
                <>
                    <label>
                        Name
                        <input value={forms.doctors.name} onChange={(event) => updateFormValue('doctors', 'name', event.target.value)} required />
                    </label>
                    <label>
                        Designation
                        <div className='admin-custom-select' ref={designationSelectRef}>
                            <button
                                type='button'
                                className={`admin-custom-select-trigger ${forms.doctors.designation ? '' : 'is-placeholder'}`}
                                onClick={() => setIsDesignationMenuOpen((open) => !open)}
                                aria-expanded={isDesignationMenuOpen}
                            >
                                {forms.doctors.designation || 'Select designation'}
                            </button>
                            {isDesignationMenuOpen && (
                                <div className='admin-custom-select-menu' role='listbox'>
                                    {doctorDesignationOptions.map((designation) => (
                                        <button
                                            type='button'
                                            key={designation}
                                            className={`admin-custom-select-option ${forms.doctors.designation === designation ? 'is-selected' : ''}`}
                                            onClick={() => {
                                                updateFormValue('doctors', 'designation', designation);
                                                setIsDesignationMenuOpen(false);
                                            }}
                                        >
                                            {designation}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <input
                                type='text'
                                className='admin-custom-select-value'
                                value={forms.doctors.designation}
                                onChange={() => {}}
                                required
                                readOnly
                                aria-hidden='true'
                                tabIndex={-1}
                            />
                        </div>
                    </label>
                </>
            )}
            {renderTable(
                'doctors',
                ['Name', 'Designation'],
                data.doctors.map((doctor) => (
                    <tr key={doctor.id}>
                        <td>{doctor.name}</td>
                        <td>{doctor.designation}</td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('doctors', doctor)}>Edit</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('doctors', doctor.id)}>Delete</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No doctors found.'
            )}
        </div>
    );

    const renderNoticesContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'notices',
                editingIds.notices ? 'Edit notice' : 'Add notice',
                <>
                    <label>
                        Title
                        <input value={forms.notices.title} onChange={(event) => updateFormValue('notices', 'title', event.target.value)} required />
                    </label>
                    <div className='admin-editor-group'>
                        <span className='admin-editor-label'>Description</span>
                        <EditorField
                            key={`notice-desc-${editingIds.notices || 'new'}`}
                            value={forms.notices.description}
                            onChange={(val) => updateFormValue('notices', 'description', val)}
                            placeholder='Write the notice details...'
                        />
                    </div>
                    <label className='admin-checkbox'>
                        <input type='checkbox' checked={forms.notices.isActive} onChange={(event) => updateFormValue('notices', 'isActive', event.target.checked)} />
                        Notice is active
                    </label>
                </>
            )}
            {renderTable(
                'notices',
                ['Title', 'Description', 'Active'],
                data.notices.map((notice) => (
                    <tr key={notice.id}>
                        <td>{notice.title}</td>
                        <td>
                            <div
                                className='admin-richtext-preview'
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.description) }}
                            />
                        </td>
                        <td>
                            <label className='admin-checkbox'>
                                <input
                                    type='checkbox'
                                    checked={Boolean(notice.isActive)}
                                    onChange={(event) => handleNoticeActiveToggle(notice.id, event.target.checked)}
                                    disabled={submitting}
                                />
                                {notice.isActive ? 'Yes' : 'No'}
                            </label>
                        </td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('notices', notice)}>Edit</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('notices', notice.id)}>Delete</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No notices found.'
            )}
        </div>
    );

    const renderOpeningTimesContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'openingTimes',
                editingIds.openingTimes ? 'Edit opening time' : 'Add opening time',
                <>
                    <label>
                        Day of Week
                        <select value={forms.openingTimes.dayOfWeek} onChange={(event) => updateFormValue('openingTimes', 'dayOfWeek', event.target.value)} disabled={Boolean(editingIds.openingTimes)}>
                            {dayOptions.map((day) => (
                                <option value={day} key={day}>{day}</option>
                            ))}
                        </select>
                    </label>
                    <label className='admin-checkbox'>
                        <input type='checkbox' checked={forms.openingTimes.isClosed} onChange={(event) => updateFormValue('openingTimes', 'isClosed', event.target.checked)} />
                        Closed all day
                    </label>
                    <div className='admin-form-split'>
                        <label>
                            Opening Time
                            <input type='time' value={forms.openingTimes.openingTime} onChange={(event) => updateFormValue('openingTimes', 'openingTime', event.target.value)} disabled={forms.openingTimes.isClosed} />
                        </label>
                        <label>
                            Closing Time
                            <input type='time' value={forms.openingTimes.closingTime} onChange={(event) => updateFormValue('openingTimes', 'closingTime', event.target.value)} disabled={forms.openingTimes.isClosed} />
                        </label>
                    </div>
                    <div className='admin-form-split'>
                        <label>
                            Evening Opening Time
                            <input type='time' value={forms.openingTimes.eveningOpeningTime} onChange={(event) => updateFormValue('openingTimes', 'eveningOpeningTime', event.target.value)} disabled={forms.openingTimes.isClosed} />
                        </label>
                        <label>
                            Evening Closing Time
                            <input type='time' value={forms.openingTimes.eveningClosingTime} onChange={(event) => updateFormValue('openingTimes', 'eveningClosingTime', event.target.value)} disabled={forms.openingTimes.isClosed} />
                        </label>
                    </div>
                </>
            )}
            {renderTable(
                'openingTimes',
                ['Day', 'Morning Opening', 'Morning Closing', 'Evening Opening', 'Evening Closing', 'Closed'],
                data.openingTimes.map((record) => (
                    <tr key={record.id}>
                        <td>{record.dayOfWeek}</td>
                        <td>{record.openingTime || '-'}</td>
                        <td>{record.closingTime || '-'}</td>
                        <td>{record.eveningOpeningTime || '-'}</td>
                        <td>{record.eveningClosingTime || '-'}</td>
                        <td>{record.isClosed ? 'Yes' : 'No'}</td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('openingTimes', record)}>Edit</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('openingTimes', record.id)}>Delete</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No opening times found.'
            )}
        </div>
    );

    const renderFeatureTogglesContent = () => (
        <div className='admin-section-stack'>
            <div className='admin-card admin-form-card'>
                <p className='admin-section-kicker'>{sectionMetaTranslated.featureToggles.title}</p>
                <h3>{sectionMetaTranslated.featureToggles.title}</h3>
                <p className='admin-section-text'>{sectionMetaTranslated.featureToggles.description}</p>
                <form
                    className='admin-form'
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSaveFeatureToggles();
                    }}
                >
                    <label className='admin-checkbox'>
                        <input
                            type='checkbox'
                            checked={forms.featureToggles.prescriptionEnabled}
                            onChange={(event) => updateFormValue('featureToggles', 'prescriptionEnabled', event.target.checked)}
                        />
                        {t('contact_modal_quick_order_prescription')}
                    </label>
                    <label className='admin-checkbox'>
                        <input
                            type='checkbox'
                            checked={forms.featureToggles.appointmentEnabled}
                            onChange={(event) => updateFormValue('featureToggles', 'appointmentEnabled', event.target.checked)}
                        />
                        {t('contact_modal_quick_make_appointment')}
                    </label>
                    <label className='admin-checkbox'>
                        <input
                            type='checkbox'
                            checked={forms.featureToggles.referralEnabled}
                            onChange={(event) => updateFormValue('featureToggles', 'referralEnabled', event.target.checked)}
                        />
                        {t('contact_modal_quick_get_referral')}
                    </label>
                    <label className='admin-checkbox'>
                        <input
                            type='checkbox'
                            checked={forms.featureToggles.phoneEnabled}
                            onChange={(event) => updateFormValue('featureToggles', 'phoneEnabled', event.target.checked)}
                        />
                        {t('contact_modal_quick_book_phone')}
                    </label>
                    <div className='admin-form-actions'>
                        <button type='submit' disabled={submitting} className='admin-btn'>
                            {submitting ? t('admin_saving') : t('admin_save_changes')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    const renderJobOpeningsContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'jobOpenings',
                editingIds.jobOpenings ? 'Edit job opening' : 'Add job opening',
                <>
                    <label>
                        Job Title
                        <input value={forms.jobOpenings.title} onChange={(event) => updateFormValue('jobOpenings', 'title', event.target.value)} required />
                    </label>
                    <div className='admin-form-split'>
                        <label>
                            Employment Type
                            <div>
                                {employmentTypeOptions.map((value) => (
                                    <label key={value} className='admin-checkbox'>
                                        <input
                                            type='checkbox'
                                            checked={normalizeEmploymentTypes(forms.jobOpenings.employmentType).includes(value)}
                                            onChange={() => toggleJobOpeningEmploymentType(value)}
                                        />
                                        {formatStatusOptionLabel(value)}
                                    </label>
                                ))}
                            </div>
                        </label>
                        <label>
                            Location
                            <input value={forms.jobOpenings.location} onChange={(event) => updateFormValue('jobOpenings', 'location', event.target.value)} required />
                        </label>
                    </div>
                    <label className='admin-checkbox'>
                        <input
                            type='checkbox'
                            checked={Boolean(forms.jobOpenings.isActive)}
                            onChange={(event) => updateFormValue('jobOpenings', 'isActive', event.target.checked)}
                        />
                        Active
                    </label>
                    <div className='admin-editor-group'>
                        <span className='admin-editor-label'>Description</span>
                        <EditorField
                            key={`desc-${editingIds.jobOpenings || 'new'}`}
                            value={forms.jobOpenings.description}
                            onChange={(val) => updateFormValue('jobOpenings', 'description', val)}
                            placeholder='Describe the role...'
                        />
                    </div>
                    <div className='admin-editor-group'>
                        <span className='admin-editor-label'>Responsibilities</span>
                        <EditorField
                            key={`resp-${editingIds.jobOpenings || 'new'}`}
                            value={forms.jobOpenings.responsibilities}
                            onChange={(val) => updateFormValue('jobOpenings', 'responsibilities', val)}
                            placeholder='List responsibilities...'
                        />
                    </div>
                    <div className='admin-editor-group'>
                        <span className='admin-editor-label'>Profile</span>
                        <EditorField
                            key={`prof-${editingIds.jobOpenings || 'new'}`}
                            value={forms.jobOpenings.profile}
                            onChange={(val) => updateFormValue('jobOpenings', 'profile', val)}
                            placeholder='Describe the ideal candidate...'
                        />
                    </div>
                    <div className='admin-editor-group'>
                        <span className='admin-editor-label'>Benefits</span>
                        <EditorField
                            key={`bene-${editingIds.jobOpenings || 'new'}`}
                            value={forms.jobOpenings.benefits}
                            onChange={(val) => updateFormValue('jobOpenings', 'benefits', val)}
                            placeholder='List benefits...'
                        />
                    </div>
                </>
            )}
            {renderTable(
                'jobOpenings',
                ['Title', 'Type', 'Location', 'Status'],
                data.jobOpenings.map((opening) => (
                    <tr key={opening.id}>
                        <td>{opening.title}</td>
                        <td>{formatEmploymentTypeLabel(opening.employmentType)}</td>
                        <td>{opening.location}</td>
                        <td>{opening.isActive ? 'Active' : 'Inactive'}</td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('jobOpenings', opening)}>Edit</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('jobOpenings', opening.id)}>Delete</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No job openings found.'
            )}
        </div>
    );

    const resolveResumeUrl = (value) => {
        if (!value) return '';
        const rawValue = String(value);
        if (rawValue.startsWith('http://') || rawValue.startsWith('https://')) {
            return rawValue;
        }
        return `/${rawValue.replace(/^\/+/, '')}`;
    };

    const renderApplicationsContent = () => (
        <div className='admin-section-stack'>
            {renderTable(
                'applications',
                [
                    {
                        key: 'name',
                        label: 'Name',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'name'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'name' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('name'),
                    },
                    {
                        key: 'email',
                        label: 'Email',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'email'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'email' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('email'),
                    },
                    {
                        key: 'phone',
                        label: 'Phone',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'phone'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'phone' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('phone'),
                    },
                    {
                        key: 'address',
                        label: 'Address',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'address'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'address' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('address'),
                    },
                    {
                        key: 'startDate',
                        label: 'Start Date',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'startDate'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'startDate' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('startDate'),
                    },
                    {
                        key: 'germanLevel',
                        label: 'German Level',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'germanLevel'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'germanLevel' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('germanLevel'),
                    },
                    {
                        key: 'workAuthorized',
                        label: 'Work Authorized',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'workAuthorized'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'workAuthorized' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('workAuthorized'),
                    },
                    {
                        key: 'gdpr',
                        label: 'GDPR',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'gdpr'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'gdpr' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('gdpr'),
                    },
                    {
                        key: 'notes',
                        label: 'Notes',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'notes'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'notes' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('notes'),
                    },
                    {
                        key: 'resume',
                        label: 'Resume',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'resume'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'resume' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('resume'),
                    },
                    {
                        key: 'jobTitle',
                        label: 'Job Applied For',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'jobTitle'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'jobTitle' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('jobTitle'),
                    },
                    {
                        key: 'submitted',
                        label: 'Submitted',
                        className: 'admin-sortable-header',
                        sortIndicatorClass: applicationsSort.key === 'submitted'
                            ? (applicationsSort.direction === 'asc' ? 'is-up' : 'is-down')
                            : 'is-down is-muted',
                        ariaSort: applicationsSort.key === 'submitted' ? (applicationsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
                        onClick: () => toggleApplicationsSort('submitted'),
                    },
                ],
                sortedApplications.map((application) => (
                    <tr key={application.id}>
                        <td>{`${application.firstName} ${application.lastName}`.trim()}</td>
                        <td>{application.email}</td>
                        <td>{application.phone}</td>
                        <td>{application.address || '-'}</td>
                        <td>{formatDateOnly(application.earliestStartDate)}</td>
                        <td>{(application.germanLanguageLevels || []).join(', ') || '-'}</td>
                        <td>{application.workAuthorization ? 'Yes' : 'No'}</td>
                        <td>{application.gdprConsent ? 'Yes' : 'No'}</td>
                        <td>
                            {application.message ? (
                                <button
                                    type='button'
                                    className='admin-note-preview'
                                    onClick={() =>
                                        setViewingApplicationNote({
                                            name: `${application.firstName || ''} ${application.lastName || ''}`.trim(),
                                            note: application.message,
                                        })
                                    }
                                >
                                    {truncateText(application.message)}
                                </button>
                            ) : (
                                '-'
                            )}
                        </td>
                        <td>
                            {application.resumePath ? (
                                <a href={resolveResumeUrl(application.resumePath)} target='_blank' rel='noreferrer'>
                                    {application.resumeOriginalName || 'Download'}
                                </a>
                            ) : (
                                '-'
                            )}
                        </td>
                        <td>{application.jobOpening?.title || '-'}</td>
                        <td>{formatDateTime(application.createdAt)}</td>
                    </tr>
                )),
                'No applications found.'
            )}
        </div>
    );

    const renderSpecialistsContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'specialists',
                editingIds.specialists ? t('admin_edit_specialist') : t('admin_add_specialist_title'),
                <>
                    <label>
                        {t('admin_specialist_name_en')}
                        <input
                            value={forms.specialists.nameEn}
                            onChange={(event) => updateFormValue('specialists', 'nameEn', event.target.value)}
                            required
                        />
                    </label>
                    <label>
                        {t('admin_specialist_name_de')}
                        <input
                            value={forms.specialists.nameDe}
                            onChange={(event) => updateFormValue('specialists', 'nameDe', event.target.value)}
                            required
                        />
                    </label>
                </>
            )}

            {renderTable(
                'specialists',
                [t('admin_specialist_name_en'), t('admin_specialist_name_de')],
                data.specialists.map((specialist) => (
                    <tr key={specialist.id}>
                        <td>{specialist.nameEn}</td>
                        <td>{specialist.nameDe}</td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('specialists', specialist)}>{t('admin_edit')}</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('specialists', specialist.id)}>{t('admin_delete')}</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No specialists found.'
            )}
        </div>
    );

    const renderReferralsContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'referrals',
                editingIds.referrals ? 'Edit referral' : 'Add referral',
                <>
                    <div className='admin-form-split'>
                        <label>
                            Specialist Type
                            <input value={forms.referrals.specialistType} onChange={(event) => updateFormValue('referrals', 'specialistType', event.target.value)} required />
                        </label>
                        <label>
                            Urgency
                            <input value={forms.referrals.urgency} onChange={(event) => updateFormValue('referrals', 'urgency', event.target.value)} placeholder='Routine / Urgent' />
                        </label>
                    </div>

                    <label>
                        Reason
                        <textarea rows='4' value={forms.referrals.reason} onChange={(event) => updateFormValue('referrals', 'reason', event.target.value)} required />
                    </label>

                    <div className='admin-form-split'>
                        <label>
                            Salutation
                            <select value={forms.referrals.salutation} onChange={(event) => updateFormValue('referrals', 'salutation', event.target.value)}>
                                {salutationOptions.map((value) => (
                                    <option value={value} key={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Insurance Method
                            <select value={forms.referrals.insuranceMethod} onChange={(event) => updateFormValue('referrals', 'insuranceMethod', event.target.value)}>
                                {insuranceOptions.map((value) => (
                                    <option value={value} key={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className='admin-form-split'>
                        <label>
                            Surname
                            <input value={forms.referrals.surname} onChange={(event) => updateFormValue('referrals', 'surname', event.target.value)} required />
                        </label>
                        <label>
                            First Name
                            <input value={forms.referrals.firstName} onChange={(event) => updateFormValue('referrals', 'firstName', event.target.value)} required />
                        </label>
                    </div>

                    <div className='admin-form-split'>
                        <label>
                            Date of Birth
                            <input type='date' value={forms.referrals.dateOfBirth} onChange={(event) => updateFormValue('referrals', 'dateOfBirth', event.target.value)} required />
                        </label>
                        <label>
                            Email
                            <input type='email' value={forms.referrals.email} onChange={(event) => updateFormValue('referrals', 'email', event.target.value)} required />
                        </label>
                    </div>

                    <div className='admin-form-split'>
                        <label>
                            Phone
                            <input value={forms.referrals.phone} onChange={(event) => updateFormValue('referrals', 'phone', event.target.value)} />
                        </label>
                        <label>
                            Status
                            <select value={forms.referrals.status} onChange={(event) => updateFormValue('referrals', 'status', event.target.value)}>
                                {referralStatusOptions.map((value) => (
                                    <option value={value} key={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <label>
                        Admin Notes
                        <textarea rows='3' value={forms.referrals.adminNotes} onChange={(event) => updateFormValue('referrals', 'adminNotes', event.target.value)} />
                    </label>

                    <label>
                        Referral Letter Path
                        <input value={forms.referrals.referralLetterPath} onChange={(event) => updateFormValue('referrals', 'referralLetterPath', event.target.value)} placeholder='/files/referrals/referral-letter.pdf' />
                    </label>

                    <div className='admin-form-split'>
                        <label className='admin-checkbox'>
                            <input type='checkbox' checked={forms.referrals.consentDataProcessing} onChange={(event) => updateFormValue('referrals', 'consentDataProcessing', event.target.checked)} />
                            Data processing consent
                        </label>
                        <label className='admin-checkbox'>
                            <input type='checkbox' checked={forms.referrals.consentMarketing} onChange={(event) => updateFormValue('referrals', 'consentMarketing', event.target.checked)} />
                            Marketing consent
                        </label>
                    </div>
                </>
            )}

            {renderTable(
                'referrals',
                [t('admin_patient'), t('admin_specialist_type'), t('admin_status')],
                data.referrals.map((referral) => (
                    <tr key={referral.id}>
                        <td>{`${referral.salutation} ${referral.firstName} ${referral.surname}`}</td>
                        <td>{referral.specialistType}</td>
                        <td>
                            <div className='admin-inline-status is-compact'>
                                <button
                                    type='button'
                                    className={`admin-inline-status-trigger admin-status-trigger ${isStatusMenuOpen('referrals', referral.id) ? 'is-open' : ''}`}
                                    onClick={(event) => toggleStatusMenu(event, 'referrals', referral.id, referral.status, referralStatusOptions)}
                                    disabled={submitting}
                                    aria-expanded={isStatusMenuOpen('referrals', referral.id)}
                                >
                                    {formatStatusOptionLabel(referral.status)}
                                </button>
                            </div>
                        </td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleViewReferral(referral)}>{t('admin_view')}</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('referrals', referral)}>{t('admin_edit')}</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('referrals', referral.id)}>{t('admin_delete')}</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No referrals found.'
            )}
        </div>
    );

    const renderPrescriptionOrdersContent = () => (
        <div className='admin-section-stack'>
            {renderSectionCard(
                'prescriptionOrders',
                editingIds.prescriptionOrders ? 'Edit prescription order' : 'Add prescription order',
                <>
                    <div className='admin-form-split'>
                        <label>
                            Salutation
                            <select value={forms.prescriptionOrders.salutation} onChange={(event) => updateFormValue('prescriptionOrders', 'salutation', event.target.value)}>
                                {salutationOptions.map((value) => (
                                    <option value={value} key={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Insurance Method
                            <select value={forms.prescriptionOrders.insuranceMethod} onChange={(event) => updateFormValue('prescriptionOrders', 'insuranceMethod', event.target.value)}>
                                {insuranceOptions.map((value) => (
                                    <option value={value} key={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className='admin-form-split'>
                        <label>
                            Surname
                            <input value={forms.prescriptionOrders.surname} onChange={(event) => updateFormValue('prescriptionOrders', 'surname', event.target.value)} required />
                        </label>
                        <label>
                            First Name
                            <input value={forms.prescriptionOrders.firstName} onChange={(event) => updateFormValue('prescriptionOrders', 'firstName', event.target.value)} required />
                        </label>
                    </div>

                    <div className='admin-form-split'>
                        <label>
                            Date of Birth
                            <input type='date' value={forms.prescriptionOrders.dateOfBirth} onChange={(event) => updateFormValue('prescriptionOrders', 'dateOfBirth', event.target.value)} required />
                        </label>
                        <label>
                            Email
                            <input type='email' value={forms.prescriptionOrders.email} onChange={(event) => updateFormValue('prescriptionOrders', 'email', event.target.value)} required />
                        </label>
                    </div>

                    <div className='admin-form-split'>
                        <label>
                            Phone
                            <input value={forms.prescriptionOrders.phone} onChange={(event) => updateFormValue('prescriptionOrders', 'phone', event.target.value)} />
                        </label>
                        <label>
                            Status
                            <select value={forms.prescriptionOrders.status} onChange={(event) => updateFormValue('prescriptionOrders', 'status', event.target.value)}>
                                {prescriptionStatusOptions.map((value) => (
                                    <option value={value} key={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <label>
                        Patient Notes
                        <textarea rows='3' value={forms.prescriptionOrders.notes} onChange={(event) => updateFormValue('prescriptionOrders', 'notes', event.target.value)} />
                    </label>

                    <label>
                        Admin Notes
                        <textarea rows='3' value={forms.prescriptionOrders.adminNotes} onChange={(event) => updateFormValue('prescriptionOrders', 'adminNotes', event.target.value)} />
                    </label>
                </>
            )}

            {renderTable(
                'prescriptionOrders',
                [t('admin_patient'), t('admin_status'), t('admin_patient_notes')],
                data.prescriptionOrders.map((order) => (
                    <tr key={order.id}>
                        <td>{`${order.salutation} ${order.firstName} ${order.surname}`}</td>
                        <td>
                            <div className='admin-inline-status is-compact'>
                                <button
                                    type='button'
                                    className={`admin-inline-status-trigger admin-status-trigger ${isStatusMenuOpen('prescriptionOrders', order.id) ? 'is-open' : ''}`}
                                    onClick={(event) => toggleStatusMenu(event, 'prescriptionOrders', order.id, order.status, prescriptionStatusOptions)}
                                    disabled={submitting}
                                    aria-expanded={isStatusMenuOpen('prescriptionOrders', order.id)}
                                >
                                    {formatStatusOptionLabel(order.status)}
                                </button>
                            </div>
                        </td>
                        <td>{order.notes || '-'}</td>
                        <td>
                            <div className='admin-table-actions'>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleViewPrescriptionOrder(order)}>{t('admin_view')}</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-ghost' onClick={() => handleEdit('prescriptionOrders', order)}>{t('admin_edit')}</button>
                                <button type='button' className='admin-btn admin-btn-small admin-btn-danger' onClick={() => handleDelete('prescriptionOrders', order.id)}>{t('admin_delete')}</button>
                            </div>
                        </td>
                    </tr>
                )),
                'No prescription orders found.'
            )}
        </div>
    );

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'doctors':
                return renderDoctorsContent();
            case 'notices':
                return renderNoticesContent();
            case 'openingTimes':
                return renderOpeningTimesContent();
            case 'featureToggles':
                return renderFeatureTogglesContent();
            case 'jobOpenings':
                return renderJobOpeningsContent();
            case 'applications':
                return renderApplicationsContent();
            case 'specialists':
                return renderSpecialistsContent();
            case 'referrals':
                return renderReferralsContent();
            case 'prescriptionOrders':
                return renderPrescriptionOrdersContent();
            default:
                return null;
        }
    };

    return (
        <>
            <div className={`page-wrapper admin-shell${isLoggedIn ? ' is-dashboard' : ''}`}>
                <div className={`container admin-container${isLoggedIn ? ' is-dashboard' : ''}`}>
                <section className='admin-hero'>
                    {isLoggedIn && !mobileMenuOpen && (
                        <button
                            type='button'
                            className='admin-mobile-menu-toggle'
                            onClick={() => setMobileMenuOpen(true)}
                            aria-label='Open navigation menu'
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    )}
                    <div className='admin-hero-content'>
                        <p className='admin-eyebrow'>{t('admin_control_center')}</p>
                        <h1>{isLoggedIn ? t('admin_dashboard') : t('admin_login')}</h1>
                        <p className='admin-subtitle'>
                            {isLoggedIn ? t('admin_dashboard_subtitle') : t('admin_login_subtitle')}
                        </p>
                    </div>
                    {isLoggedIn && (
                        <div className='admin-hero-actions'>
                            <div className='admin-hero-actions-desktop'>
                                <div className='admin-lang-switcher'>
                                    <button
                                        type='button'
                                        className={`admin-lang-btn ${currentLanguage === 'en' ? 'is-active' : ''}`}
                                        onClick={() => handleLanguageChange('en')}
                                    >
                                        <Us /> EN
                                    </button>
                                    <button
                                        type='button'
                                        className={`admin-lang-btn ${currentLanguage === 'de' ? 'is-active' : ''}`}
                                        onClick={() => handleLanguageChange('de')}
                                    >
                                        <De /> DE
                                    </button>
                                </div>
                                <button type='button' onClick={handleLogout} disabled={submitting} className='admin-btn admin-btn-ghost'>
                                    {t('admin_logout')}
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {error && <div className='admin-alert error'>{error}</div>}
                {isLoggedIn && message && <div className='admin-alert success'>{message}</div>}

                {!isLoggedIn ? (
                    <section className='admin-login-wrap'>
                        <div className='admin-card admin-login-card'>
                            <p className='admin-section-kicker'>{t('admin_access')}</p>
                            <h2>{t('admin_sign_in')}</h2>
                            <form className='admin-form' onSubmit={handleLogin}>
                                <label>
                                    {t('admin_email')}
                                    <input type='email' value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t('admin_email_placeholder')} required />
                                </label>
                                <label>
                                    {t('admin_password')}
                                    <div className='admin-password-wrap'>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(event) => setPassword(event.target.value)}
                                            placeholder={t('admin_password_placeholder')}
                                            required
                                        />
                                        <button
                                            type='button'
                                            className='admin-password-toggle'
                                            onClick={() => setShowPassword((current) => !current)}
                                            aria-label={showPassword ? t('admin_hide_password') : t('admin_show_password')}
                                            aria-pressed={showPassword}
                                        >
                                            <i className={showPassword ? 'ti-close' : 'ti-eye'}></i>
                                        </button>
                                    </div>
                                </label>
                                <button type='submit' disabled={loading} className='admin-btn'>
                                    {loading ? t('admin_signing_in') : t('admin_sign_in_button')}
                                </button>
                            </form>
                        </div>
                    </section>
                ) : (
                    <section className='admin-dashboard-layout'>
                        {mobileMenuOpen && (
                            <div className='admin-mobile-overlay' onClick={() => setMobileMenuOpen(false)}></div>
                        )}
                        <aside className={`admin-card admin-sidebar ${mobileMenuOpen ? 'is-mobile-open' : ''}`}>
                            <div className='admin-sidebar-brand'>
                                <button
                                    type='button'
                                    className='admin-mobile-close'
                                    onClick={() => setMobileMenuOpen(false)}
                                    aria-label='Close navigation menu'
                                >
                                    <i className='ti-close'></i>
                                </button>
                                <p className='admin-section-kicker'>{t('admin_navigation')}</p>
                                <h2>{t('admin_panel')}</h2>
                                <p className='admin-sidebar-text'>{admin?.email || t('admin_signed_in_admin')}</p>
                            </div>
                            <nav className='admin-sidebar-nav' aria-label={t('admin_navigation')}>
                                {sidebarItemsTranslated.map((item) => (
                                    <button
                                        type='button'
                                        key={item.key}
                                        className={`admin-nav-item${activeSection === item.key ? ' is-active' : ''}`}
                                        onClick={() => {
                                            setActiveSection(item.key);
                                            setOpenFormSection(null);
                                            clearFlash();
                                            setMobileMenuOpen(false);
                                        }}
                                    >
                                        <span>{item.label}</span>
                                        <small>{data[item.key].length}</small>
                                    </button>
                                ))}
                            </nav>
                            <div className='admin-sidebar-footer'>
                                <div className='admin-lang-switcher'>
                                    <button
                                        type='button'
                                        className={`admin-lang-btn ${currentLanguage === 'en' ? 'is-active' : ''}`}
                                        onClick={() => handleLanguageChange('en')}
                                    >
                                        <Us /> EN
                                    </button>
                                    <button
                                        type='button'
                                        className={`admin-lang-btn ${currentLanguage === 'de' ? 'is-active' : ''}`}
                                        onClick={() => handleLanguageChange('de')}
                                    >
                                        <De /> DE
                                    </button>
                                </div>
                                <button type='button' onClick={handleLogout} disabled={submitting} className='admin-btn admin-btn-ghost'>
                                    {t('admin_logout')}
                                </button>
                            </div>
                        </aside>

                        <div className='admin-main'>
                            <div className='admin-card admin-topbar'>
                                <div>
                                    <p className='admin-section-kicker'>{sectionMetaTranslated[activeSection].title}</p>
                                    <h2>{sectionMetaTranslated[activeSection].title}</h2>
                                    <p className='admin-section-text'>{sectionMetaTranslated[activeSection].description}</p>
                                </div>
                            </div>

                            {loading ? (
                                <div className='admin-card'>
                                    <p className='admin-section-text'>{t('admin_loading_data')}</p>
                                </div>
                            ) : (
                                renderActiveSection()
                            )}
                        </div>
                    </section>
                )}
                </div>
            </div>
            {activeStatusMenu &&
                createPortal(
                    <div
                        className='admin-inline-status-menu admin-status-portal'
                        style={{
                            position: 'fixed',
                            left: `${activeStatusMenu.left}px`,
                            top: `${activeStatusMenu.top}px`,
                            width: `${activeStatusMenu.width}px`,
                            transform: 'none',
                        }}
                    >
                        {activeStatusMenu.options.map((value) => (
                            <button
                                type='button'
                                key={value}
                                className={`admin-inline-status-option ${activeStatusMenu.currentValue === value ? 'is-selected' : ''}`}
                                onClick={() => {
                                    if (activeStatusMenu.currentValue !== value) {
                                        handleQuickStatusUpdate(activeStatusMenu.section, activeStatusMenu.id, value);
                                    } else {
                                        setActiveStatusMenu(null);
                                    }
                                }}
                            >
                                {formatStatusOptionLabel(value)}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
            {viewingPrescriptionOrder &&
                createPortal(
                    <div className='admin-modal-overlay' onClick={handleCloseViewModal}>
                        <div className='admin-modal' onClick={(e) => e.stopPropagation()}>
                            <div className='admin-modal-header'>
                                <h2>{t('admin_prescription_order_details')}</h2>
                                <button type='button' className='admin-modal-close' onClick={handleCloseViewModal} aria-label={t('admin_close')}>
                                    <i className='ti-close'></i>
                                </button>
                            </div>
                            <div className='admin-modal-body'>
                                <div className='admin-modal-grid'>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_patient')}</label>
                                        <p>{`${viewingPrescriptionOrder.salutation} ${viewingPrescriptionOrder.firstName} ${viewingPrescriptionOrder.surname}`}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_email')}</label>
                                        <p>{viewingPrescriptionOrder.email}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_phone')}</label>
                                        <p>{viewingPrescriptionOrder.phone || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_date_of_birth')}</label>
                                        <p>{toLocalDateInput(viewingPrescriptionOrder.dateOfBirth)}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_insurance_method')}</label>
                                        <p>{viewingPrescriptionOrder.insuranceMethod}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_status')}</label>
                                        <p>{formatStatusOptionLabel(viewingPrescriptionOrder.status)}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>{t('admin_patient_notes')}</label>
                                        <p>{viewingPrescriptionOrder.notes || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>{t('admin_admin_notes')}</label>
                                        <p>{viewingPrescriptionOrder.adminNotes || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>{t('admin_medications_list')}</label>
                                        <div className='admin-modal-medications'>
                                            {viewingPrescriptionOrder.prescriptionItems && viewingPrescriptionOrder.prescriptionItems.length > 0 ? (
                                                viewingPrescriptionOrder.prescriptionItems.map((item) => {
                                                    const medication = data.medications.find((m) => m.id === item.medicationId);
                                                    return (
                                                        <div key={item.medicationId} className='admin-modal-medication-item'>
                                                            {medication ? medication.name : `Medication ID: ${item.medicationId}`}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <p>-</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_estimated_ready_time')}</label>
                                        <p>{viewingPrescriptionOrder.estimatedReadyTime ? formatDateTime(viewingPrescriptionOrder.estimatedReadyTime) : '-'}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_fulfilled_at')}</label>
                                        <p>{viewingPrescriptionOrder.fulfilledAt ? formatDateTime(viewingPrescriptionOrder.fulfilledAt) : '-'}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_consent_data_processing')}</label>
                                        <p>{viewingPrescriptionOrder.consentDataProcessing ? t('admin_yes') : t('admin_no')}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_consent_marketing')}</label>
                                        <p>{viewingPrescriptionOrder.consentMarketing ? t('admin_yes') : t('admin_no')}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_created_at')}</label>
                                        <p>{formatDateTime(viewingPrescriptionOrder.createdAt)}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_updated_at')}</label>
                                        <p>{formatDateTime(viewingPrescriptionOrder.updatedAt)}</p>
                                    </div>
                                </div>
                            </div>
                            <div className='admin-modal-footer'>
                                <button type='button' className='admin-btn admin-btn-ghost' onClick={handleCloseViewModal}>
                                    {t('admin_close')}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            {viewingReferral &&
                createPortal(
                    <div className='admin-modal-overlay' onClick={handleCloseReferralModal}>
                        <div className='admin-modal' onClick={(e) => e.stopPropagation()}>
                            <div className='admin-modal-header'>
                                <h2>{t('admin_referral_details')}</h2>
                                <button type='button' className='admin-modal-close' onClick={handleCloseReferralModal} aria-label={t('admin_close')}>
                                    <i className='ti-close'></i>
                                </button>
                            </div>
                            <div className='admin-modal-body'>
                                <div className='admin-modal-grid'>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_patient')}</label>
                                        <p>{`${viewingReferral.salutation} ${viewingReferral.firstName} ${viewingReferral.surname}`}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_email')}</label>
                                        <p>{viewingReferral.email}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_phone')}</label>
                                        <p>{viewingReferral.phone || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_date_of_birth')}</label>
                                        <p>{toLocalDateInput(viewingReferral.dateOfBirth)}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_specialist_type')}</label>
                                        <p>{viewingReferral.specialistType}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_urgency')}</label>
                                        <p>{viewingReferral.urgency || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>{t('admin_reason_for_referral')}</label>
                                        <p>{viewingReferral.reason}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_insurance_method')}</label>
                                        <p>{viewingReferral.insuranceMethod}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_status')}</label>
                                        <p>{formatStatusOptionLabel(viewingReferral.status)}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>{t('admin_admin_notes')}</label>
                                        <p>{viewingReferral.adminNotes || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>{t('admin_referral_letter_path')}</label>
                                        <p>{viewingReferral.referralLetterPath || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_consent_data_processing')}</label>
                                        <p>{viewingReferral.consentDataProcessing ? t('admin_yes') : t('admin_no')}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_consent_marketing')}</label>
                                        <p>{viewingReferral.consentMarketing ? t('admin_yes') : t('admin_no')}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_created_at')}</label>
                                        <p>{formatDateTime(viewingReferral.createdAt)}</p>
                                    </div>
                                    <div className='admin-modal-field'>
                                        <label>{t('admin_updated_at')}</label>
                                        <p>{formatDateTime(viewingReferral.updatedAt)}</p>
                                    </div>
                                </div>
                            </div>
                            <div className='admin-modal-footer'>
                                <button type='button' className='admin-btn admin-btn-ghost' onClick={handleCloseReferralModal}>
                                    {t('admin_close')}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            {viewingApplicationNote &&
                createPortal(
                    <div className='admin-modal-overlay' onClick={handleCloseApplicationNote}>
                        <div className='admin-modal' onClick={(e) => e.stopPropagation()}>
                            <div className='admin-modal-header'>
                                <h2>Application Notes</h2>
                                <button type='button' className='admin-modal-close' onClick={handleCloseApplicationNote} aria-label={t('admin_close')}>
                                    <i className='ti-close'></i>
                                </button>
                            </div>
                            <div className='admin-modal-body'>
                                <div className='admin-modal-grid'>
                                    <div className='admin-modal-field'>
                                        <label>Applicant</label>
                                        <p>{viewingApplicationNote.name || '-'}</p>
                                    </div>
                                    <div className='admin-modal-field admin-modal-field-full'>
                                        <label>Notes</label>
                                        <p className='admin-note-full'>{viewingApplicationNote.note}</p>
                                    </div>
                                </div>
                            </div>
                            <div className='admin-modal-footer'>
                                <button type='button' className='admin-btn admin-btn-ghost' onClick={handleCloseApplicationNote}>
                                    {t('admin_close')}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default AdminPage;